import React from 'react';

interface RosterCellProps {
  /** Comma-separated names that are still current. */
  active: string;
  /** Comma-separated names that are not — rendered struck through beside them. */
  inactive: string;
}

const split = (v: string) =>
  String(v ?? '').split(',').map((s) => s.trim()).filter(Boolean);

/**
 * A roster column whose entries are not all still current — the EDI LCs "LC List".
 *
 * The grid used to show only the current names, by filtering them in the query. That does
 * not shorten a list so much as delete the ROW: a handle whose every LC had left produced
 * no grid line at all, and since the Add drawer hides handles that already have a mapping,
 * there was no way to put a working LC back on it from 360. Both halves come back now, and
 * the difference is carried visually instead.
 *
 * So the cell answers two questions at a glance: how much of the roster is real ("4 / 12"),
 * and whether anyone is on the account at all. The count appears only when someone HAS
 * left — a fully-current roster is the normal case and needs no ornament.
 */
export const RosterCell: React.FC<RosterCellProps> = ({ active, inactive }) => {
  const live = split(active);
  const gone = split(inactive);
  if (!live.length && !gone.length) return null;

  const total = live.length + gone.length;
  return (
    <span
      className="inline-flex min-w-0 items-baseline gap-1.5"
      title={[...live, ...gone.map((n) => `${n} (inactive)`)].join(', ')}
    >
      {gone.length > 0 && (
        <span
          className={`shrink-0 rounded-full px-1.5 py-0.5 text-[0.625rem] font-semibold tabular-nums ${
            live.length ? 'bg-warn text-warn-fg' : 'bg-danger text-danger-fg'
          }`}
        >
          {live.length} / {total}
        </span>
      )}
      <span className="truncate">
        {live.join(', ')}
        {live.length > 0 && gone.length > 0 && ', '}
        {gone.length > 0 && (
          <span className="text-danger-fg line-through">{gone.join(', ')}</span>
        )}
      </span>
      {/* Nobody is working this account. Said in words rather than left to be inferred
          from a row of struck-through names. */}
      {!live.length && (
        <span className="shrink-0 text-[0.625rem] font-semibold uppercase text-danger-fg">
          No active LC
        </span>
      )}
    </span>
  );
};

export default RosterCell;
